import { useApolloClient } from '@apollo/client';
import { MaterialType, type TagSortOrder } from '@mnd-frontend/graphql-schema';
import { useToasts } from '@mnd-frontend/ui';
import { purgeImageUsages } from '@mnd-frontend/utils';
import { useTranslation } from 'react-i18next';
import { MediaPicker } from '../../components/materials/MediaPicker';
import { useCurrentPressroom } from '../../contexts/CurrentPressroomContext';
import { useUpdateTagMutation } from '../../generated/hooks';
import type { TagManagementTagFragment } from '../../generated/types';

const TagImageUploadModal = ({
  tag,
  onClose,
  query,
  perPage,
  page,
  sortOrder,
}: {
  tag: TagManagementTagFragment;
  onClose: () => void;
  query: string;
  perPage: number;
  page: number;
  sortOrder: TagSortOrder;
}) => {
  const { pressroomId } = useCurrentPressroom();
  const [t] = useTranslation();
  const { showFailureToast } = useToasts();
  const selectedImageId = tag?.image?.id;
  const cache = useApolloClient().cache;
  const [updateTag] = useUpdateTagMutation({
    onCompleted: response => {
      purgeImageUsages(cache, response.tagUpdate?.newImage);
      purgeImageUsages(cache, response.tagUpdate?.previousImage);
    },
  });

  return (
    <MediaPicker
      show={!!tag}
      onDismiss={onClose}
      isSelected={m => m.__typename === 'Image' && selectedImageId === m.id}
      onSelect={async image => {
        if (image.__typename !== 'Image' || !image.thumbnail) return;

        try {
          const result = await updateTag({
            variables: {
              id: tag.id,
              imageId: image.thumbnail.id,
              query,
              perPage,
              page,
              sortOrder,
              pressroomId,
            },
          });
          const error = result.data?.tagUpdate?.error;
          if (!error) {
            return onClose();
          }
          showFailureToast(error?.message ?? t('common_general_error'));
        } catch (err) {
          showFailureToast(t('common_general_error'));
        }
      }}
      selectableTypes={[MaterialType.Image]}
      title={t('publish_pr-edit_image-bank-gallery-title')}
      searchPlaceholder={t('publish_edit-page_image-bank_search-placeholder')}
      data-testid="image-bank-in-rte-container"
    />
  );
};

export default TagImageUploadModal;
